/**
 * NotificationBell — top-nav bell icon with unread badge + popover preview.
 *
 * - Polls /notifications/unread-count every 30s (and once on mount).
 * - On open, fetches the latest 20 notifications.
 * - Clicking an item marks it read and (if it has a link) navigates.
 * - "Mark all read" and a "View all" link to /notifications for the full list.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import {
  Bell, MessageSquare, BookOpen, Megaphone, Beaker, Info,
  CheckCheck, Inbox, Linkedin,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

// Icon lookup — the backend hints an icon name but we render a real
// component here.  Falls back to a generic Info bubble.
const ICONS = {
  'message-square': MessageSquare,
  'book-open': BookOpen,
  'megaphone': Megaphone,
  'beaker': Beaker,
  'bell': Bell,
};

const iconFor = (n) => {
  if (n?.type === 'forum_reply' || n?.type === 'forum_mention') return MessageSquare;
  if (n?.type === 'lesson_added' || n?.type === 'course_updated') return BookOpen;
  if (n?.type === 'admin_broadcast') return Megaphone;
  if (n?.type === 'batch_milestone') return Beaker;
  return ICONS[n?.icon] || Info;
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const NotificationBell = ({ compact = false }) => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`);
      setUnread(data?.unread_count ?? 0);
    } catch (_) { /* ignore transient errors */ }
  }, [API]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications`, { params: { limit: 20 } });
      setItems(data?.items || []);
      setUnread(data?.unread_count ?? 0);
    } catch (_) { /* ignore transient errors */ }
    setLoading(false);
  }, [API]);

  // Poll unread count every 30s.
  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleClickItem = async (n) => {
    // Optimistically mark read
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
      try { await axios.put(`${API}/notifications/${n.id}/read`); } catch (_) { /* ignore */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
    } catch (_) { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative inline-flex items-center justify-center rounded-full border border-border bg-background text-foreground/80 hover:text-foreground hover:bg-muted transition-colors ${compact ? 'h-9 w-9' : 'h-9 w-9'}`}
          data-testid="notification-bell"
          aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold leading-[18px] text-center shadow-sm"
              data-testid="notification-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] max-w-[92vw] p-0 overflow-hidden"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center">
              <Bell className="w-3.5 h-3.5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-extrabold text-foreground">Notifications</div>
              <div className="text-[11px] text-muted-foreground">{unread ? `${unread} unread` : 'All caught up'}</div>
            </div>
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={handleMarkAll}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto divide-y divide-border" data-testid="notification-list">
          {loading && (
            <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-8 flex flex-col items-center text-center gap-2" data-testid="notification-empty">
              <Inbox className="w-8 h-8 text-muted-foreground opacity-60" />
              <div className="text-sm font-semibold text-foreground">You&apos;re all caught up</div>
              <div className="text-xs text-muted-foreground">
                Forum replies, new lessons and announcements will appear here.
              </div>
            </div>
          )}
          {!loading && items.map((n) => {
            const Icon = iconFor(n);
            const shareUrl = n?.metadata?.share_url;
            const shareCert = n?.metadata?.share_cert_number;
            return (
              <div
                key={n.id}
                className={`px-4 py-3 hover:bg-muted/50 transition-colors ${!n.is_read ? 'bg-primary/[.04]' : ''}`}
                data-testid={`notification-item-${n.id}`}
              >
                <button
                  type="button"
                  onClick={() => handleClickItem(n)}
                  className="w-full text-left flex items-start gap-3"
                >
                  <div className={`h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center ${!n.is_read ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-foreground truncate flex items-center gap-2">
                      {n.title}
                      {!n.is_read && <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />}
                    </div>
                    {n.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/80 mt-1">{relTime(n.created_at)}</div>
                  </div>
                </button>
                {shareUrl && (
                  <div className="mt-2 pl-11">
                    <a
                      href={shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full font-bold px-3 h-8 text-xs bg-[#0A66C2] text-white hover:bg-[#0a5aad] transition-colors"
                      data-testid={`notification-share-${n.id}`}
                      title="Open LinkedIn share composer pre-filled with your certificate — it will unfurl into the branded gold-seal card."
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Linkedin className="w-3.5 h-3.5" /> Share the win
                    </a>
                    {shareCert && (
                      <span className="ml-2 text-[10px] font-mono text-muted-foreground">
                        {shareCert}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-border bg-muted/20 flex items-center justify-between">
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="text-xs font-semibold text-primary hover:underline"
            data-testid="notification-view-all"
          >
            View all →
          </button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={fetchList}
            data-testid="notification-refresh"
          >
            Refresh
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
